/**
 * Consent-cookie reader shared by analytics providers (PostHog, Clarity,
 * Meta Pixel). Kept in sync with `src/components/consent/ConsentBanner.tsx`
 * which writes the cookie. Use this BEFORE initializing any analytics SDK
 * that captures session recordings, identities, or advertising IDs.
 *
 * The cookie carries three states plus "unchosen":
 *   'granted'   — explicit full accept (analytics + advertising)
 *   'denied'    — explicit reject (nothing)
 *   'analytics' — region-implied analytics-only default (opt-out regions, GA4
 *                 Path B.1); analytics flows but advertising stays denied
 *   absent      — no choice yet → treat as denied for privacy
 *
 * Use readConsent() to gate ADVERTISING (explicit accept only); use
 * hasAnalyticsConsent() to gate ANALYTICS (explicit accept OR region default).
 */

export const CONSENT_COOKIE = 'bizrnr_consent';

export type ConsentState = 'granted' | 'denied' | null;

/** Raw cookie value, including the region-implied 'analytics' state (GA4 Path B.1). */
function rawConsent(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.split('; ').find((c) => c.startsWith(CONSENT_COOKIE + '='));
  if (!m) return null;
  return decodeURIComponent(m.slice(CONSENT_COOKIE.length + 1));
}

/**
 * Explicit consent state for AD / marketing consumers (Meta, TikTok, Google Ads,
 * Clarity). The region-implied 'analytics' value reads as null here on purpose so
 * advertising signals stay denied until the user explicitly Accepts.
 */
export function readConsent(): ConsentState {
  const v = rawConsent();
  return v === 'granted' || v === 'denied' ? v : null;
}

/**
 * Analytics consent for ANALYTICS consumers (PostHog basic capture, the GA4
 * Measurement Protocol relay). True whenever analytics_storage is granted:
 * explicit 'granted', either region-implied default ('analytics' or the opt-out
 * 'analytics_ads'), or 'ads_optout' (advertising off but analytics kept). Only an
 * explicit 'denied' / no-choice reads false.
 */
export function hasAnalyticsConsent(): boolean {
  const v = rawConsent();
  return (
    v === 'granted' || v === 'analytics' || v === 'analytics_ads' || v === 'ads_optout'
  );
}

/**
 * Advertising consent for AD-PIXEL consumers (Google Ads remarketing, Meta,
 * TikTok). True for an explicit 'granted' OR the region-implied opt-out default
 * 'analytics_ads' (US/other under CCPA/CPRA, where ad_storage is granted by
 * default). The analytics-only 'analytics' / 'ads_optout' states and an explicit
 * 'denied' all read false.
 *
 * NOTE: session-recording consumers (Clarity, PostHog full capture) deliberately
 * use readConsent() === 'granted' instead — they require an explicit Accept and
 * are NOT enabled by the opt-out ad default.
 */
export function hasAdsConsent(): boolean {
  const v = rawConsent();
  return v === 'granted' || v === 'analytics_ads';
}
